import ReactECharts from "echarts-for-react";
import { RevenueMetric } from "@/services/metrics";
import { formatCurrency } from "@/lib/utils";
import { Loader2 } from "lucide-react";

interface RevenueChartProps {
  data: RevenueMetric[];
  isLoading: boolean;
}

export function RevenueChart({ data, isLoading }: RevenueChartProps) {
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-[300px]">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className="flex items-center justify-center h-[300px] text-muted-foreground text-sm">
        Sin datos para el período seleccionado
      </div>
    );
  }

  const dates = data.map((d) => d.snapshotDate);

  const option = {
    tooltip: {
      trigger: "axis",
      axisPointer: { type: "shadow" },
      backgroundColor: "#fff",
      borderColor: "#e2e8f0",
      borderWidth: 1,
      textStyle: { color: "#1e293b", fontSize: 12 },
      formatter: (params) => {
        let str = `<div style="font-weight:600;margin-bottom:4px">${params[0].axisValue}</div>`;
        params.forEach((p) => {
          str += `<div style="display:flex;align-items:center;gap:6px;margin:2px 0">
            <span style="display:inline-block;width:8px;height:8px;border-radius:50%;background:${p.color}"></span>
            <span style="color:#64748b">${p.seriesName}:</span>
            <span style="font-weight:600">$${formatCurrency(Number(p.value))}</span>
          </div>`;
        });
        return str;
      },
    },
    legend: {
      data: ["Ingresos Totales", "Ticket Promedio"],
      bottom: 0,
      textStyle: { color: "#64748b", fontSize: 11 },
      icon: "roundRect",
      itemWidth: 10,
      itemHeight: 6,
    },
    grid: { left: "3%", right: "4%", bottom: "15%", top: "6%", containLabel: true },
    xAxis: {
      type: "category",
      data: dates,
      axisLine: { lineStyle: { color: "#e2e8f0" } },
      axisLabel: { color: "#94a3b8", fontSize: 11 },
    },
    yAxis: [
      {
        type: "value",
        name: "Ingresos",
        axisLine: { show: false },
        splitLine: { lineStyle: { color: "#f1f5f9", type: "dashed" } },
        axisLabel: {
          color: "#94a3b8",
          fontSize: 10,
          formatter: (v) => `$${formatCurrency(v)}`,
        },
      },
      {
        type: "value",
        name: "Ticket",
        axisLine: { show: false },
        splitLine: { show: false },
        axisLabel: {
          color: "#94a3b8",
          fontSize: 10,
          formatter: (v) => `$${formatCurrency(v)}`,
        },
      },
    ],
    series: [
      {
        name: "Ingresos Totales",
        type: "bar",
        yAxisIndex: 0,
        data: data.map((d) => Number(d.totalRevenue)),
        barMaxWidth: 40,
        itemStyle: {
          color: {
            type: "linear",
            x: 0, y: 0, x2: 0, y2: 1,
            colorStops: [
              { offset: 0, color: "#3B82F6" },
              { offset: 1, color: "#93C5FD" },
            ],
          },
          borderRadius: [4, 4, 0, 0],
        },
      },
      {
        name: "Ticket Promedio",
        type: "line",
        yAxisIndex: 1,
        smooth: true,
        symbol: "circle",
        symbolSize: 6,
        data: data.map((d) => Number(d.avgTicket)),
        lineStyle: { color: "#F59E0B", width: 2.5 },
        itemStyle: { color: "#F59E0B" },
      },
    ],
  };

  return <ReactECharts option={option} style={{ height: 300 }} opts={{ renderer: "svg" }} />;
}
